import { useEffect, useMemo, useState } from 'react';
import api, { fmtRp, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, ScanBarcode, Plus, Minus, Trash2, Printer, ShoppingCart, Footprints, Banknote, QrCode, ArrowRightLeft, CreditCard } from 'lucide-react';
import Receipt from '../components/Receipt';
import BarcodeScanner from '../components/BarcodeScanner';

export default function Transaksi() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [branch, setBranch] = useState(isAdmin ? (branches[0]?.id || 'balaraja') : user.branch);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [sizeProduct, setSizeProduct] = useState(null);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [payment, setPayment] = useState('cash');
  const [cashPaid, setCashPaid] = useState('');
  const [custName, setCustName] = useState('');
  const [custWa, setCustWa] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = (q = '') => {
    api.get('/products', { params: { search: q } }).then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  };

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    const iv = setInterval(() => load(search), 15000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [search]);

  const stockOf = (v) => v.stock?.[branch] ?? 0;

  const addToCart = (product, variant) => {
    const existing = cart.find((c) => c.product_id === product.id && c.size === variant.size);
    const inCart = existing ? existing.qty : 0;
    if (stockOf(variant) <= inCart) {
      toast.error(`Stok ${product.article} ukuran ${variant.size} habis`);
      return;
    }
    if (existing) {
      setCart(cart.map((c) => (c === existing ? { ...c, qty: c.qty + 1 } : c)));
    } else {
      setCart([...cart, {
        product_id: product.id, article: product.article, brand: product.brand,
        size: variant.size, price: variant.price, qty: 1, maxStock: stockOf(variant),
      }]);
    }
    setSizeProduct(null);
    toast.success(`${product.article} uk ${variant.size} masuk keranjang`);
  };

  const setQty = (idx, delta) => {
    const item = cart[idx];
    const next = item.qty + delta;
    if (next <= 0) {
      setCart(cart.filter((_, i) => i !== idx));
    } else if (next > item.maxStock) {
      toast.error(`Stok maksimal ${item.maxStock}`);
    } else {
      setCart(cart.map((c, i) => (i === idx ? { ...c, qty: next } : c)));
    }
  };

  const subtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const discountAmount = useMemo(() => {
    const v = Number(discountValue) || 0;
    if (discountType === 'percent') return Math.round(subtotal * Math.min(Math.max(v, 0), 100) / 100);
    if (discountType === 'nominal') return Math.min(Math.max(v, 0), subtotal);
    return 0;
  }, [discountType, discountValue, subtotal]);
  const total = subtotal - discountAmount;
  const cashNum = Number(cashPaid) || 0;
  const change = payment === 'cash' ? cashNum - total : 0;

  const handleScan = async (code) => {
    try {
      const { data } = await api.get(`/products/barcode/${code}`);
      setSizeProduct(data.product);
    } catch {
      toast.error('Produk dengan barcode ini tidak ditemukan');
    }
  };

  const submit = async () => {
    if (cart.length === 0) return toast.error('Keranjang masih kosong');
    if (payment === 'cash' && cashNum < total) return toast.error('Uang tunai kurang dari total');
    setSubmitting(true);
    try {
      const { data } = await api.post('/transactions', {
        branch,
        items: cart.map((c) => ({ product_id: c.product_id, size: c.size, qty: c.qty })),
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        payment_method: payment,
        cash_paid: payment === 'cash' ? cashNum : total,
        customer_name: custName,
        customer_wa: custWa,
      });
      setReceipt(data);
      setCart([]);
      setDiscountType('none'); setDiscountValue(''); setCashPaid(''); setCustName(''); setCustWa('');
      toast.success(`Transaksi ${data.invoice_no} berhasil`);
      load(search);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="pos-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Transaksi</h2>
          <p className="text-sm text-muted-foreground">Pilih artikel, wajib pilih ukuran, lalu bayar</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2" data-testid="pos-branch-select">
            {branches.map((b) => (
              <button key={b.id} onClick={() => { setBranch(b.id); setCart([]); }} data-testid={`pos-branch-${b.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${branch === b.id ? 'bg-brand text-white border-brand' : 'bg-surface border-line text-muted-foreground'}`}>
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 xl:col-span-8 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari artikel / merk..."
                className="lodi-input pl-9" data-testid="pos-search-input" />
            </div>
            <button onClick={() => setScanOpen(true)} data-testid="pos-scan-button"
              className="px-4 rounded-lg bg-elevated border border-line hover:border-brand hover:text-brand transition-colors flex items-center gap-2 text-sm font-medium">
              <ScanBarcode className="w-5 h-5" /> <span className="hidden sm:inline">Scan</span>
            </button>
          </div>

          {products.length === 0 ? (
            <div className="lodi-card p-10 text-center text-muted-foreground" data-testid="pos-empty-products">
              <Footprints className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Belum ada produk. Tambahkan lewat menu Stok.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pos-product-grid">
              {products.map((p) => {
                const totalStock = p.variants.reduce((s, v) => s + stockOf(v), 0);
                const prices = p.variants.map((v) => v.price);
                const minPrice = Math.min(...prices);
                return (
                  <button key={p.id} onClick={() => setSizeProduct(p)} data-testid={`product-card-${p.id}`}
                    className="lodi-card p-4 text-left active:scale-[0.98] group">
                    <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center mb-3 group-hover:bg-brand group-hover:text-white transition-colors">
                      <Footprints className="w-5 h-5" />
                    </div>
                    <p className="font-semibold text-sm leading-tight line-clamp-2">{p.article}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.brand || 'Tanpa merk'} • {p.category}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-display font-bold text-emerald-400 text-sm">{fmtRp(minPrice)}</span>
                      <span className={`text-[11px] font-semibold ${totalStock === 0 ? 'text-brand' : 'text-muted-foreground'}`}>
                        {totalStock === 0 ? 'Habis' : `${totalStock} psg`}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6 space-y-4">
          <div className="lodi-card p-5" data-testid="cart-panel">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-brand" />
              <h3 className="font-display font-bold">Keranjang</h3>
              <span className="ml-auto text-xs text-muted-foreground">{cart.length} item</span>
            </div>

            {cart.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="cart-empty">Keranjang kosong</p>
            ) : (
              <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {cart.map((c, i) => (
                  <div key={i} className="bg-elevated rounded-lg p-3" data-testid={`cart-item-${i}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{c.article}</p>
                        <p className="text-xs text-muted-foreground">Uk {c.size} • {fmtRp(c.price)}</p>
                      </div>
                      <button onClick={() => setCart(cart.filter((_, x) => x !== i))} data-testid={`cart-remove-${i}`}
                        className="text-muted-foreground hover:text-brand transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(i, -1)} data-testid={`cart-minus-${i}`}
                          className="w-7 h-7 rounded-md bg-surface border border-line flex items-center justify-center hover:border-brand"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-8 text-center font-bold text-sm">{c.qty}</span>
                        <button onClick={() => setQty(i, 1)} data-testid={`cart-plus-${i}`}
                          className="w-7 h-7 rounded-md bg-surface border border-line flex items-center justify-center hover:border-brand"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="font-semibold text-sm">{fmtRp(c.price * c.qty)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-line mt-4 pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Nama pembeli" className="lodi-input" data-testid="customer-name-input" />
                <input value={custWa} onChange={(e) => setCustWa(e.target.value)} placeholder="No. WhatsApp" className="lodi-input" data-testid="customer-wa-input" />
              </div>

              <div>
                <span className="lodi-label">Diskon</span>
                <div className="flex gap-2">
                  {[['none', 'Tanpa'], ['percent', '%'], ['nominal', 'Rp']].map(([v, l]) => (
                    <button key={v} onClick={() => { setDiscountType(v); setDiscountValue(''); }} data-testid={`discount-type-${v}`}
                      className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${discountType === v ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                      {l}
                    </button>
                  ))}
                  {discountType !== 'none' && (
                    <input type="number" min="0" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percent' ? 'Persen' : 'Nominal'} className="lodi-input flex-1" data-testid="discount-value-input" />
                  )}
                </div>
              </div>

              <div>
                <span className="lodi-label">Pembayaran</span>
                <div className="grid grid-cols-4 gap-2">
                  {[['cash', 'Tunai', Banknote], ['qris', 'QRIS', QrCode], ['transfer', 'Transfer', ArrowRightLeft], ['debit', 'Debit', CreditCard]].map(([v, l, Icon]) => (
                    <button key={v} onClick={() => setPayment(v)} data-testid={`payment-${v}`}
                      className={`px-2 py-2.5 rounded-lg text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${payment === v ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                      <Icon className="w-4 h-4" /> {l}
                    </button>
                  ))}
                </div>
              </div>

              {payment === 'cash' && (
                <div>
                  <span className="lodi-label">Uang Diterima</span>
                  <input type="number" min="0" value={cashPaid} onChange={(e) => setCashPaid(e.target.value)} className="lodi-input" data-testid="cash-paid-input" placeholder="0" />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[50000, 100000, 150000, 200000].map((n) => (
                      <button key={n} onClick={() => setCashPaid(String(n))} data-testid={`quick-cash-${n}`}
                        className="px-2.5 py-1 rounded-md bg-elevated border border-line text-xs font-semibold hover:border-brand">{n / 1000}rb</button>
                    ))}
                    <button onClick={() => setCashPaid(String(total))} data-testid="quick-cash-exact"
                      className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">Uang Pas</button>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 pt-2 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span data-testid="cart-subtotal">{fmtRp(subtotal)}</span></div>
                {discountAmount > 0 && <div className="flex justify-between text-amber-400"><span>Diskon</span><span data-testid="cart-discount">-{fmtRp(discountAmount)}</span></div>}
                <div className="flex justify-between font-display font-extrabold text-xl pt-1 border-t border-line">
                  <span>Total</span><span className="text-emerald-400" data-testid="cart-total">{fmtRp(total)}</span>
                </div>
                {payment === 'cash' && cashNum > 0 && (
                  <div className={`flex justify-between font-semibold ${change < 0 ? 'text-brand' : 'text-blue-400'}`}>
                    <span>Kembali</span><span data-testid="cart-change">{fmtRp(Math.max(change, 0))}</span>
                  </div>
                )}
              </div>

              <button onClick={submit} disabled={submitting || cart.length === 0} data-testid="pay-button"
                className="w-full py-3.5 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold tracking-wide transition-all active:scale-[0.98] shadow-lg shadow-brand/25">
                {submitting ? 'MEMPROSES...' : 'BAYAR & CETAK STRUK'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!sizeProduct} onOpenChange={(v) => !v && setSizeProduct(null)}>
        <DialogContent className="bg-surface border-line max-w-md" data-testid="size-select-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{sizeProduct?.article}</DialogTitle>
            <p className="text-xs text-muted-foreground">{sizeProduct?.brand} — pilih ukuran terlebih dahulu</p>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2.5 mt-2">
            {sizeProduct?.variants.map((v) => {
              const st = stockOf(v);
              return (
                <button key={v.size} disabled={st === 0} onClick={() => addToCart(sizeProduct, v)} data-testid={`size-chip-${v.size}`}
                  className={`h-14 rounded-xl border font-display font-bold text-lg flex flex-col items-center justify-center transition-all active:scale-95 ${
                    st === 0 ? 'border-line text-muted-foreground/40 cursor-not-allowed' : 'border-line hover:border-brand hover:text-brand bg-elevated'
                  }`}>
                  {v.size}
                  <span className={`text-[10px] font-sans font-medium ${st === 0 ? 'text-brand/60' : 'text-muted-foreground'}`}>{st === 0 ? 'habis' : `${st} psg`}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="bg-surface border-line max-w-sm" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Transaksi Berhasil</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto py-2">
            <Receipt tx={receipt} />
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => window.print()} data-testid="print-receipt-button"
              className="flex-1 py-3 rounded-xl bg-brand hover:bg-branddark text-white font-display font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
              <Printer className="w-4 h-4" /> Cetak Struk
            </button>
            <button onClick={() => setReceipt(null)} data-testid="new-transaction-button"
              className="flex-1 py-3 rounded-xl bg-elevated border border-line font-semibold hover:border-linehi transition-colors">
              Transaksi Baru
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} title="Scan Barcode Produk" />
    </div>
  );
}
